import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  input,
  ChangeDetectorRef,
} from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { min, Subscription } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'app-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
})
export class FilterComponent implements OnDestroy, OnInit, OnChanges {
  @Input() partType: any;
  selectedSort = 'newToOld';

  selected = false;

  currentView: ViewMode = 'grid';

  @Output() viewChanged = new EventEmitter<ViewMode>();
  sortByIcon = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="sort-icon" style="width: 20px; height: 20px">
  <path d="M2 16L6 16M2 10L10 10M2 4L14 4" stroke="black" stroke-width="1.5" stroke-linecap="round"></path>
</svg>`;

  // SVG for dropdown icon (used in all other buttons)
  dropdownIcon = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="dropdown-icon" style="width: 20px; height: 20px">
  <path d="M5 7.5L9.41074 11.9107C9.73618 12.2362 10.2638 12.2362 10.5893 11.9107L15 7.5" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"></path>
</svg>`;

  filters: any[] = [];

  sortOptions = [
    { label: 'New to Old', value: 'newToOld' },
    { label: 'Old to New', value: 'oldToNew' },
    { label: 'Price: Low to High', value: 'priceLowHigh' },
    { label: 'Price: High to Low', value: 'priceHighLow' },
  ];

  yearList = [2025, 2024, 2023, 2022, 2021];
  filtersData: any;
  selectedFilters: any[] = [];
  brands: any;
  sellerSearch: string = '';
  locationSearch: string = '';
  userData: any;
  vehicleForm!: FormGroup;
  modelList: any;
  varientList: any;
  brandslist: any;

  @Input() categories: CategoryItem[] = [];
  @Input() data: any;
  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() filterClose = new EventEmitter<void>();
  Sidefilter: boolean = false;

  selectedCount = 0;
  filteredResultsCount = 0;
  activeSection: string | null = 'categories';
  activeTab: string = 'Categories';
  IMAGES: any;
  hasViewedResults = false; // Track if View Results button has been clicked
  selectedVehicle: any = []; // Track the selected vehicle

  categorySearch: string = '';
  searchText: string = '';

  // Add new properties for tracking filter counts
  filterTypeCounts: { [key: string]: number } = {
    Categories: 0,
    Brand: 0,
    'Mfg. Year': 0,
    Seller: 0,
    Location: 0,
    'Price Range': 0,
    Grade: 0,
    Vehicle: 0,
  };

  // Add property for total filtered results
  totalFilteredResults: number = 0;
  previewData: any = {};
  isRegistrationNoEntered: boolean = false;
  noVehicleFound: boolean = false;
  isRegistrationFieldFilled: boolean = false; // Track if registration field has any value
  private isInitialLoadComplete: boolean = false; // Track if initial load is complete
  private hasCalledApiFromLoad: boolean = false; // Prevent duplicate API calls

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private apiService: ApiService,
    private loaderService: LoaderService,
    private subjectBehaviour: BehaviourSubjectService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
  ) {}

  isMobile: boolean = false;
  private resizeHandler?: () => void;

  sub: Subscription = new Subscription();

  ngOnInit() {
    // Build filters based on part type
    this.updateFiltersByPartType();
    // Only check mobile view and add event listeners if we're in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.vehicleForm = this.fb.group({
        registrationNo: [''], // Validation will be added conditionally
        brand: [''],
        model: [''],
        year: [''],
        variant: [''],
      });
      try {
        const userDataString = localStorage.getItem('userData1');
        if (userDataString) {
          // Check if the data looks encrypted (starts with base64 pattern like "U2FsdGVkX1")
          if (userDataString.startsWith('U2FsdGVkX1')) {
            // Data is encrypted, set empty object as fallback
            this.userData = {};
          } else {
            this.userData = JSON.parse(userDataString);
          }
        } else {
          this.userData = {};
        }
      } catch (error) {
        console.error('Error parsing userData1 from localStorage:', error);
        this.userData = {};
      }
      this.searchText = '';
      // Subscribe to search term changes to clear search text when needed
      this.sub.add(
        this.subjectBehaviour.searchTerm$.subscribe((term) => {
          if (term === '') {
            this.searchText = '';
            // Also remove search chips from selectedFilters
            this.selectedFilters = this.selectedFilters.filter(
              (filter: any) => filter.type !== 'search',
            );
          }
        }),
      );

      // Load saved filters from localStorage
      this.loadFiltersFromLocalStorage();

      this.checkMobileView();
      this.getFilterList();
      this.getBrandList();

      // Add form change listeners
      this.sub.add(
        this.vehicleForm.valueChanges.subscribe(() => {
          this.updateSelectedFilters();
          this.saveFiltersToLocalStorage();
        }),
      );

      // Store reference to the bound function for cleanup
      this.resizeHandler = () => this.checkMobileView();
      // Listen for window resize events
      window.addEventListener('resize', this.resizeHandler);
    }

    console.log('FilterComponent initialized with partType:', this.filtersData);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      // Handle data changes here - you can add specific logic based on your needs
      // For example, you might want to update filters or trigger other actions
      this.onDataChanged(changes['data'].currentValue);
    }
    if (changes['partType']) {
      this.updateFiltersByPartType();
    }
  }

  onDataChanged(newData: any) {
    // Reload filter list when data changes (including when it becomes null)
    // This ensures all categories are shown when a category is deselected
    this.getFilterList();
    // Add any specific logic you want to execute when data changes
    // For example, you might want to update the filter list or reset certain filters
  }

  minValue: number = 0;
  maxValue: number = 0; // 10000;
  pendingMinValue?: number;
  pendingMaxValue?: number;

  selectedRadio: any = null;
  isPriceRangeModified: boolean = false; // Track if user has modified price range

  radioOptions: any[] = [];

  toggleSelected(): void {
    this.selected = !this.selected;
  }

  onSortChange(): void {
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();
  }

  toggleView(view: ViewMode): void {
    if (this.currentView !== view) {
      this.currentView = view;
      this.viewChanged.emit(view);
    }
  }

  mobiletoggle: boolean = false;
  toggleViewmobile(): void {
    this.mobiletoggle = !this.mobiletoggle;
    if (this.mobiletoggle) {
      this.currentView = 'list';
      this.viewChanged.emit(this.currentView);
    } else {
      this.currentView = 'grid';
      this.viewChanged.emit(this.currentView);
    }
  }

  removeFilter(filterValue: string) {
    // Check if it's a search filter
    const searchFilter = this.selectedFilters.find(
      (filter: any) => filter.value === filterValue && filter.type === 'search',
    );

    if (searchFilter) {
      // Handle search filter removal
      this.removeSearchChip(filterValue);
    }

    // Remove from selectedFilters array
    this.selectedFilters = this.selectedFilters.filter(
      (filter: any) => filter.value !== filterValue,
    );

    // Also remove from the actual filter data
    this.removeFromFilterData(filterValue);

    // Update subject behavior and trigger API call
    this.updateSubjectAndApiCall();
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();

    // If no filters remain, reset the View Results flag
    if (this.selectedFilters.length === 0) {
      this.hasViewedResults = false;
    }
  }

  updateSubjectAndApiCall() {
    // Build the updated filter parameters (same logic as viewResults)
    // Get selected years from both filter data and vehicle form
    const filterYears =
      this.filtersData?.year
        ?.filter((y: any) => y.selected)
        .map((y: any) => y.name) || [];
    const vehicleYear = this.vehicleForm.get('year')?.value;
    const selectedYears = [...filterYears];
    if (vehicleYear && !selectedYears.includes(vehicleYear)) {
      selectedYears.push(vehicleYear);
    }

    // Add selected vehicle year from garage if available
    if (
      this.selectedVehicle &&
      this.selectedVehicle.year_of_manufacturing &&
      !selectedYears.includes(this.selectedVehicle.year_of_manufacturing)
    ) {
      selectedYears.push(this.selectedVehicle.year_of_manufacturing);
    }

    const selectedGrades =
      this.filtersData?.quality
        ?.filter((q: any) => q.selected)
        .map((q: any) => q.name) || [];

    // Get selected brands from both filter data and vehicle form
    const filterBrands =
      this.filtersData?.make
        ?.filter((b: any) => b.selected)
        .map((b: any) => b.name) || [];
    const vehicleBrand = this.vehicleForm.get('brand')?.value;
    const selectedBrands = [...filterBrands];
    if (vehicleBrand && !selectedBrands.includes(vehicleBrand)) {
      selectedBrands.push(vehicleBrand);
    }

    // Add selected vehicle brand from garage if available
    if (
      this.selectedVehicle &&
      this.selectedVehicle.make &&
      !selectedBrands.includes(this.selectedVehicle.make)
    ) {
      selectedBrands.push(this.selectedVehicle.make);
    }

    const selectedLocations =
      this.filtersData?.location
        ?.filter((l: any) => l.selected)
        .map((l: any) => l.name) || [];
    const selectedSellers =
      this.filtersData?.seller
        ?.filter((s: any) => s.selected)
        .map((s: any) => s.name) || [];

    // Get selected categories from both filter data and vehicle form
    const filterCategories = this.getSelectedCategories() || [];
    const vehicleModel = this.vehicleForm.get('model')?.value;
    const selectedCategories = [...filterCategories];
    if (vehicleModel && !selectedCategories.includes(vehicleModel)) {
      selectedCategories.push(vehicleModel);
    }

    // Add selected vehicle model from garage if available
    if (
      this.selectedVehicle &&
      this.selectedVehicle.model &&
      !selectedCategories.includes(this.selectedVehicle.model)
    ) {
      selectedCategories.push(this.selectedVehicle.model);
    }

    const selectedSubcategories = this.getSelectedSubcategories();

    // Get search text from search chips
    const searchTexts = this.selectedFilters
      .filter((filter: any) => filter.type === 'search')
      .map((filter: any) => filter.value);

    // Get selected variants from vehicle form
    const vehicleVariant = this.vehicleForm.get('variant')?.value;
    const selectedVariants = [];
    if (vehicleVariant && vehicleVariant.trim() !== '') {
      selectedVariants.push(vehicleVariant);
    }

    // Get selected model from vehicle form for parent_model field
    const selectedVehicleModel = this.vehicleForm.get('model')?.value;

    // Add selected vehicle variant from garage if available
    if (
      this.selectedVehicle &&
      this.selectedVehicle.variant &&
      !selectedVariants.includes(this.selectedVehicle.variant)
    ) {
      selectedVariants.push(this.selectedVehicle.variant);
    }

    const updatedFilters = {
      years: selectedYears,
      grades: selectedGrades,
      brands: selectedBrands,
      locations: selectedLocations,
      sellers: selectedSellers,
      categories: selectedCategories,
      subcategories: selectedSubcategories,
      price_range: this.selectedRadio
        ? [this.selectedRadio.min, this.selectedRadio.max]
        : this.isPriceRangeModified
          ? [this.minValue, this.maxValue]
          : null,
      sort_by: this.selectedSort,
      search_text: searchTexts.length > 0 ? searchTexts.join(' ') : '',
      variants: selectedVariants,
      variant: this.selectedVehicle?.variant,
      model:
        selectedVehicleModel && selectedVehicleModel.trim() !== ''
          ? selectedVehicleModel
          : this.selectedVehicle?.model,
    };

    // Update subject behavior
    this.subjectBehaviour.setSelectedFilters(updatedFilters);

    // Trigger API call with updated parameters
    this.makeApiCallWithFilters(updatedFilters);
  }

  makeApiCallWithFilters(filters: any) {
    // Build API parameters
    const apiParams: any = {
      limit: 50,
      offset: 0,
      filter_type: this.partType,
    };

    // Add filter parameters
    if (filters.years && filters.years.length > 0) {
      apiParams.year = Array.isArray(filters.years)
        ? filters.years.join(',')
        : filters.years;
    }
    if (filters.grades && filters.grades.length > 0) {
      apiParams.quality = filters.grades.join(',');
    }
    if (filters.brands && filters.brands.length > 0) {
      apiParams.make = Array.isArray(filters.brands)
        ? filters.brands.join(',')
        : filters.brands;
    }
    if (filters.locations && filters.locations.length > 0) {
      apiParams.location = filters.locations.join(',');
    }
    if (filters.sellers && filters.sellers.length > 0) {
      apiParams.seller = filters.sellers.join(',');
    }
    if (filters.categories && filters.categories.length > 0) {
      apiParams.category = filters.categories.join(',');
    }
    if (filters.subcategories && filters.subcategories.length > 0) {
      apiParams.subcategory = filters.subcategories.join(',');
    }

    // Add price range if user has modified it or selected a radio option
    const maxSliderValue = this.getSliderMax();
    if (
      this.selectedRadio ||
      (this.isPriceRangeModified &&
        (this.minValue > this.getSliderMin() || this.maxValue < maxSliderValue))
    ) {
      apiParams.price_range = this.selectedRadio
        ? [this.selectedRadio.min, this.selectedRadio.max]
        : [this.minValue, this.maxValue];
      // apiParams.min_price = this.minValue;
      // apiParams.max_price = this.maxValue;
    }

    // Add search text (always include it, even if empty)
    apiParams.search_text = filters.search_text || '';

    // Add selected variants from vehicle form
    if (filters.variants && filters.variants.length > 0) {
      apiParams.variant_list = filters.variants;
    }

    // Add selected vehicle parameters if available
    if (this.selectedVehicle) {
      if (this.selectedVehicle.make) {
        apiParams.make = this.selectedVehicle.make;
      }
      if (this.selectedVehicle.model) {
        apiParams.model = this.selectedVehicle.model;
      }
      if (
        this.selectedVehicle.variant &&
        (!filters.variants || filters.variants.length === 0)
      ) {
        apiParams.variant_list = [this.selectedVehicle.variant];
      }
      if (this.selectedVehicle.year_of_manufacturing) {
        apiParams.year = this.selectedVehicle.year_of_manufacturing;
      }
    }

    // Add vehicle form parameters if available
    if (this.vehicleForm) {
      const formBrand = this.vehicleForm.get('brand')?.value;
      const formModel = this.vehicleForm.get('model')?.value;
      const formYear = this.vehicleForm.get('year')?.value;
      const formVariant = this.vehicleForm.get('variant')?.value;

      if (formBrand && formBrand.trim() !== '') {
        apiParams.make = formBrand;
      }
      if (formModel && formModel.trim() !== '') {
        apiParams.model = formModel;
      }
      if (formYear && formYear.toString().trim() !== '') {
        apiParams.year = formYear.toString();
      }
      if (formVariant && formVariant.trim() !== '' && !apiParams.variant_list) {
        apiParams.variant_list = [formVariant];
      }
    }

    // Make the API call
    this.loaderService.show();
    this.sub.add(
      this.apiService.getFilters(apiParams).subscribe({
        next: (value) => {
          if (value.success) {
            // You can update the product list or other data here
            // Emit the updated data to parent component
            this.selectionChange.emit(value.data);
          }
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error in API call after filter removal:', err);
          this.loaderService.hide();
        },
      }),
    );
  }

  removeFromFilterData(filterValue: string) {
    // Remove from categories (including subcategories)
    if (this.filtersData?.category) {
      this.filtersData.category.forEach((cat: any) => {
        if (cat.name === filterValue) {
          cat.selected = false;
        }
        if (cat.children) {
          cat.children.forEach((child: any) => {
            if ((child.label || child.name) === filterValue) {
              child.selected = false;
            }
          });
        }
      });
    }

    // Remove from brands
    if (this.filtersData?.make) {
      this.filtersData.make.forEach((brand: any) => {
        if (brand.name === filterValue) {
          brand.selected = false;
        }
      });
    }

    // Remove from years
    if (this.filtersData?.year) {
      this.filtersData.year.forEach((year: any) => {
        if (year.name === filterValue) {
          year.selected = false;
        }
      });
    }

    // Remove from quality/grades
    if (this.filtersData?.quality) {
      this.filtersData.quality.forEach((quality: any) => {
        if (quality.name === filterValue) {
          quality.selected = false;
        }
      });
    }

    // Remove from locations
    if (this.filtersData?.location) {
      this.filtersData.location.forEach((location: any) => {
        if (location.name === filterValue) {
          location.selected = false;
        }
      });
    }

    // Remove from sellers
    if (this.filtersData?.seller) {
      this.filtersData.seller.forEach((seller: any) => {
        if (seller.name === filterValue) {
          seller.selected = false;
        }
      });
    }

    // Remove price range filter
    if (filterValue.startsWith('price_')) {
      this.minValue = this.getSliderMin();
      this.maxValue = this.getSliderMax();
      this.selectedRadio = null;
      this.isPriceRangeModified = false;
    }

    // Remove vehicle form data
    if (this.vehicleForm) {
      const brand = this.vehicleForm.get('brand')?.value;
      const model = this.vehicleForm.get('model')?.value;
      const variant = this.vehicleForm.get('variant')?.value;
      const year = this.vehicleForm.get('year')?.value;
      // const registrationNo = this.vehicleForm.get('registrationNo')?.value;

      // If it's the combined vehicle filter or matches any individual vehicle component, clear all
      if (
        filterValue === 'vehicle_combined' ||
        brand === filterValue ||
        model === filterValue ||
        variant === filterValue ||
        year?.toString() === filterValue
      ) {
        //  || registrationNo === filterValue
        // Clear all vehicle form fields
        this.vehicleForm.get('registrationNo')?.setValue('');
        this.vehicleForm.get('brand')?.setValue('');
        this.vehicleForm.get('model')?.setValue('');
        this.vehicleForm.get('variant')?.setValue('');
        this.vehicleForm.get('year')?.setValue('');
        // Clear dependent lists
        this.modelList = [];
        this.varientList = [];
      }
    }

    // Remove selected vehicle from garage if it matches the filter value
    if (
      this.selectedVehicle &&
      (filterValue === 'vehicle_garage' ||
        this.selectedVehicle.make === filterValue)
    ) {
      this.selectedVehicle = null;
    }

    // Update counts
    this.updateSelectedCount();
    this.updateFilteredResultsCount();
  }

  addToSelectedFilters(filter: { type: string; value: string; label: string }) {
    // Validate that filter has valid values before adding
    if (
      !filter ||
      !filter.value ||
      !filter.label ||
      filter.value.trim() === '' ||
      filter.label.trim() === ''
    ) {
      return;
    }

    // Check if filter already exists
    const exists = this.selectedFilters.find(
      (f: any) => f.value === filter.value,
    );
    if (!exists) {
      this.selectedFilters.push(filter);
    }
  }

  // Utility method to clean up empty filters
  cleanupEmptyFilters() {
    this.selectedFilters = this.selectedFilters.filter(
      (filter: any) =>
        filter &&
        filter.value &&
        filter.label &&
        filter.value.trim() !== '' &&
        filter.label.trim() !== '',
    );
  }

  // Method to generate comprehensive vehicle label
  generateVehicleLabel(): string {
    if (!this.vehicleForm) return '';

    const brand = this.vehicleForm.get('brand')?.value;
    const model = this.vehicleForm.get('model')?.value;
    const year = this.vehicleForm.get('year')?.value;
    const variant = this.vehicleForm.get('variant')?.value;

    const parts: string[] = [];

    // Combine available vehicle details (excluding registration number)
    if (brand && brand.trim() !== '') parts.push(brand);
    if (model && model.trim() !== '') parts.push(model);
    if (year && year.toString().trim() !== '') parts.push(year.toString());
    if (variant && variant.trim() !== '') parts.push(variant);

    return parts.length > 0 ? parts.join(' ') : '';
  }

  // Method to check if any vehicle filter is selected
  hasVehicleFilter(): boolean {
    if (!this.vehicleForm) return false;

    const registrationNo = this.vehicleForm.get('registrationNo')?.value;
    const brand = this.vehicleForm.get('brand')?.value;
    const model = this.vehicleForm.get('model')?.value;
    const year = this.vehicleForm.get('year')?.value;
    const variant = this.vehicleForm.get('variant')?.value;

    return !!(
      registrationNo?.trim() ||
      brand?.trim() ||
      model?.trim() ||
      year?.toString()?.trim() ||
      variant?.trim()
    );
  }

  updateSelectedFilters() {
    // Preserve search chips before clearing the array (but only valid ones)
    const searchChips = this.selectedFilters.filter(
      (filter: any) =>
        filter.type === 'search' && filter.value && filter.value.trim() !== '',
    );

    // Clear the array
    this.selectedFilters = [];

    // Add selected categories (following parent/subcategory logic)
    if (this.filtersData?.category) {
      this.filtersData.category.forEach((cat: any) => {
        // Check if any subcategories are selected
        const hasSelectedSubcategories =
          cat.children?.some((child: any) => child.selected) || false;

        if (cat.selected && !hasSelectedSubcategories) {
          // Parent is selected and no subcategories are selected - show only parent
          this.addToSelectedFilters({
            type: 'category',
            value: cat.name,
            label: cat.name,
          });
        } else if (hasSelectedSubcategories) {
          // Subcategories are selected - show only the selected subcategories, not parent
          cat.children.forEach((child: any) => {
            if (child.selected) {
              this.addToSelectedFilters({
                type: 'category',
                value: child.label || child.name,
                label: child.label || child.name,
              });
            }
          });
        }
      });
    }

    // Add selected brands
    if (this.filtersData?.make) {
      this.filtersData.make.forEach((brand: any) => {
        if (brand.selected) {
          this.addToSelectedFilters({
            type: 'brand',
            value: brand.name,
            label: brand.name,
          });
        }
      });
    }

    // Add selected years
    if (this.filtersData?.year) {
      this.filtersData.year.forEach((year: any) => {
        if (year.selected) {
          this.addToSelectedFilters({
            type: 'year',
            value: year.name,
            label: year.name,
          });
        }
      });
    }

    // Add selected quality/grades
    if (this.filtersData?.quality) {
      this.filtersData.quality.forEach((quality: any) => {
        if (quality.selected) {
          this.addToSelectedFilters({
            type: 'quality',
            value: quality.name,
            label: quality.name,
          });
        }
      });
    }

    // Add selected locations
    if (this.filtersData?.location) {
      this.filtersData.location.forEach((location: any) => {
        if (location.selected) {
          this.addToSelectedFilters({
            type: 'location',
            value: location.name,
            label: location.name,
          });
        }
      });
    }

    // Add selected sellers
    if (this.filtersData?.seller) {
      this.filtersData.seller.forEach((seller: any) => {
        if (seller.selected) {
          this.addToSelectedFilters({
            type: 'seller',
            value: seller.name,
            label: seller.name,
          });
        }
      });
    }

    // Add vehicle form data if available - combine all vehicle data into single chip
    if (this.vehicleForm && this.hasVehicleFilter()) {
      const vehicleLabel = this.generateVehicleLabel();
      if (vehicleLabel) {
        this.addToSelectedFilters({
          type: 'vehicle',
          value: 'vehicle_combined',
          label: vehicleLabel,
        });
      }
    }
    // Add selected vehicle from garage if available (only if no form data)
    else if (this.selectedVehicle) {
      // Create a detailed label for the selected vehicle
      let vehicleLabel = this.selectedVehicle.make;
      if (this.selectedVehicle.model) {
        vehicleLabel += ` ${this.selectedVehicle.model}`;
      }
      if (this.selectedVehicle.year_of_manufacturing) {
        vehicleLabel += ` (${this.selectedVehicle.year_of_manufacturing})`;
      }
      if (this.selectedVehicle.variant) {
        vehicleLabel += ` - ${this.selectedVehicle.variant}`;
      }

      this.addToSelectedFilters({
        type: 'vehicle',
        value: 'vehicle_garage',
        label: vehicleLabel,
      });
    }

    // Add price range filter if user has modified it
    const maxSliderValue = this.getSliderMax();
    if (
      this.selectedRadio ||
      (this.isPriceRangeModified &&
        (this.minValue > this.getSliderMin() || this.maxValue < maxSliderValue))
    ) {
      const priceLabel = `₹${this.minValue.toLocaleString()} - ₹${this.maxValue.toLocaleString()}`;
      this.addToSelectedFilters({
        type: 'price',
        value: `price_${this.minValue}_${this.maxValue}`,
        label: priceLabel,
      });
    }

    // Restore search chips
    searchChips.forEach((chip: any) => {
      this.addToSelectedFilters(chip);
    });

    // Clean up any empty filters
    this.cleanupEmptyFilters();

    // Update filter counts and results
    this.updateSelectedCount();

    // Save filters to localStorage
    this.saveFiltersToLocalStorage();
  }

  // VERTICLE FILTERS

  onSliderChange() {
    // Mark as modified when user changes values
    this.isPriceRangeModified = true;

    const maxSliderValue = this.getSliderMax();
    const minSliderValue = this.getSliderMin();
    const step = this.getSliderStep();

    // Allow thumbs to touch (minValue = maxValue) with a 1-step gap
    if (this.minValue > this.maxValue - step) {
      this.minValue = this.maxValue - step;
    }
    if (this.maxValue < this.minValue + step) {
      this.maxValue = this.minValue + step;
    }

    // Ensure values are within bounds
    if (this.minValue < minSliderValue) {
      this.minValue = minSliderValue;
    }
    if (this.maxValue > maxSliderValue) {
      this.maxValue = maxSliderValue;
    }

    this.selectedRadio = null; // Deselect radio when slider is manually used
    this.updateSelectedFilters(); // Update selected filters when price range changes
    this.updateSelectedCount();
    this.saveFiltersToLocalStorage();
  }

  onRadioSelect(option: any) {
    this.selectedRadio = option;
    this.isPriceRangeModified = true;
    this.minValue = option.min;
    this.maxValue = option.max;
    this.updateSelectedFilters(); // Update selected filters when price range changes
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();
  }

  private checkMobileView(): void {
    // Only check mobile view if we're in browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Check if it's a mobile device OR if the screen width is less than 768px (tablet/mobile breakpoint)
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ) ||
        window.innerWidth < 768
      ) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.modalService.closeModal('filter-sidebar');
    // Remove the event listener to prevent memory leaks
    if (this.resizeHandler && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    // Unsubscribe from all subscriptions
    this.sub.unsubscribe();
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;

    if (this.activeTab === 'Vehicle' && this.userData.id) {
      this.getBrandList();
      this.getGarage();
    } else if (this.activeTab === 'Vehicle' && !this.userData.id) {
      this.getBrandList();
    }
  }
  toggleSection(section: string): void {
    // If clicking the already active section, close it
    if (this.activeSection === section) {
      this.activeSection = null;
    } else {
      this.activeSection = section;
    }
  }
  toggleExpand(item: CategoryItem): void {
    item.expanded = !item.expanded;
  }

  toggleSelection(item: CategoryItem, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    item.selected = checkbox.checked;

    // Check if this is a parent category
    const isParentCategory = this.filtersData?.category?.includes(item);

    if (isParentCategory && item.children) {
      if (item.selected) {
        // Parent selected - deselect all subcategories
        this.updateChildSelection(item.children, false);
      } else {
        // Parent deselected - do nothing with children
      }
    } else {
      // This is a subcategory - update parent selection
      this.updateParentSelection(item);
    }

    this.updateSelectedCount();
    this.updateFilteredResultsCount();
    this.emitSelectedCategories();
    this.updateSelectedFilters(); // Add this line to update selected filters display

    // Save filters to localStorage
    this.saveFiltersToLocalStorage();
  }

  sideFilterPopup(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('button');
    const filterIndex = Array.from(
      button?.parentElement?.children || [],
    ).indexOf(button as Element);

    if (filterIndex >= 0 && filterIndex < this.filters.length) {
      const filter = this.filters[filterIndex];

      // Map filter names to tab names
      const filterToTabMap: { [key: string]: string } = {
        sort: 'Sort By',
        brand: 'Brand',
        quality: 'Grade',
        mfgYear: 'Mfg. Year',
        categories: 'Categories',
        seller: 'Seller',
        grade: 'Grade',
        priceRange: 'Price Range',
        location: 'Location',
      };

      // Set the active tab based on the filter clicked
      const tabName = filterToTabMap[filter.name];
      if (tabName) {
        this.setActiveTab(tabName);
        this.Sidefilter = true; // Open the sidebar popup
        this.modalService.openModal('filter-sidebar', () => this.closeFilters());
      }
    }
  }

  private updateFiltersByPartType(): void {
    // Base filters without quality
    const baseFilters = [
      { name: 'sort', label: 'Sort By', iconType: 'sort' },
      { name: 'brand', label: 'Brand', iconType: 'dropdown' },
      { name: 'mfgYear', label: 'Mfg. Year', iconType: 'dropdown' },
      { name: 'categories', label: 'Categories', iconType: 'dropdown' },
      { name: 'seller', label: 'Seller', iconType: 'dropdown' },
      { name: 'grade', label: 'Grade', iconType: 'dropdown' },
      { name: 'priceRange', label: 'Price Range', iconType: 'dropdown' },
    ] as { name: string; label: string; iconType: string }[];

    // If Scrap Part, insert quality after brand
    if ((this.partType || '').toLowerCase() === 'scrap part'.toLowerCase()) {
      this.filters = [
        baseFilters[0], // sort
        baseFilters[1], // brand
        { name: 'quality', label: 'Quality', iconType: 'dropdown' },
        ...baseFilters.slice(2),
      ];
    } else {
      this.filters = baseFilters;
    }
  }

  private buildDynamicFilters(): void {
    if (!this.filtersData) {
      return;
    }

    const dynamicFilters: any[] = [
      { name: 'sort', label: 'Sort By', iconType: 'sort' },
    ];

    // Add filters based on available data in the API response
    if (this.filtersData.location && this.filtersData.location.length > 0) {
      dynamicFilters.push({
        name: 'location',
        label: 'Location',
        iconType: 'dropdown',
      });
    }

    if (
      this.filtersData.price_range &&
      this.filtersData.price_range.length > 0
    ) {
      dynamicFilters.push({
        name: 'priceRange',
        label: 'Price Range',
        iconType: 'dropdown',
      });
    }

    if (this.filtersData.category && this.filtersData.category.length > 0) {
      dynamicFilters.push({
        name: 'categories',
        label: 'Categories',
        iconType: 'dropdown',
      });
    }

    // Add other filters if they exist in the response
    if (this.filtersData.make && this.filtersData.make.length > 0) {
      dynamicFilters.push({
        name: 'brand',
        label: 'Brand',
        iconType: 'dropdown',
      });
    }

    if (this.filtersData.year && this.filtersData.year.length > 0) {
      dynamicFilters.push({
        name: 'mfgYear',
        label: 'Mfg. Year',
        iconType: 'dropdown',
      });
    }

    if (this.filtersData.seller && this.filtersData.seller.length > 0) {
      dynamicFilters.push({
        name: 'seller',
        label: 'Seller',
        iconType: 'dropdown',
      });
    }

    if (this.filtersData.quality && this.filtersData.quality.length > 0) {
      dynamicFilters.push({
        name: 'grade',
        label: 'Grade',
        iconType: 'dropdown',
      });
    }

    this.filters = dynamicFilters;
  }

  viewResults() {
    // Set flag to show selected filters after View Results is clicked
    this.hasViewedResults = true;

    // Save filters to localStorage when applying results
    this.saveFiltersToLocalStorage();

    // Trigger API call with the selected filters
    this.updateSubjectAndApiCall();

    this.closeFilters();
  }

  updateChildSelection(children: CategoryItem[], isSelected: boolean): void {
    children.forEach((child) => {
      child.selected = isSelected;
      if (child.children) {
        this.updateChildSelection(child.children, isSelected);
      }
    });
  }

  updateParentSelection(item: CategoryItem): void {
    // Find the parent of this item in the category tree
    const findParent = (
      categories: CategoryItem[],
      targetItem: CategoryItem,
    ): CategoryItem | null => {
      for (const category of categories) {
        if (category.children) {
          for (const child of category.children) {
            if (child === targetItem) {
              return category;
            }
          }
        }
      }
      return null;
    };

    const parent = findParent(this.filtersData?.category || [], item);
    if (parent) {
      // Check if any subcategories are selected
      const someChildrenSelected =
        parent.children?.some((child) => child.selected) || false;

      // If any subcategory is selected, deselect the parent
      if (someChildrenSelected) {
        parent.selected = false;
      }
      // If no subcategories are selected, keep parent as is
    }
  }

  getSelectedCategories(): string[] {
    const selectedCategories: string[] = [];

    if (this.filtersData?.category) {
      this.filtersData.category.forEach((cat: any) => {
        // Check if any subcategories are selected
        const hasSelectedSubcategories =
          cat.children?.some((child: any) => child.selected) || false;

        if (cat.selected && !hasSelectedSubcategories) {
          // Parent is selected and no subcategories are selected - send only parent
          selectedCategories.push(cat.name);
        }
        // Note: Subcategories are handled separately in getSelectedSubcategories()
      });
    }

    return selectedCategories;
  }

  getSelectedSubcategories(): string[] {
    const selectedSubcategories: string[] = [];

    if (this.filtersData?.category) {
      this.filtersData.category.forEach((cat: any) => {
        // Check if any subcategories are selected
        const hasSelectedSubcategories =
          cat.children?.some((child: any) => child.selected) || false;

        if (hasSelectedSubcategories) {
          // Subcategories are selected - collect them
          cat.children.forEach((child: any) => {
            if (child.selected) {
              selectedSubcategories.push(child.label || child.name);
            }
          });
        }
      });
    }

    return selectedSubcategories;
  }

  updateSelectedCount(): void {
    this.selectedCount = this.countSelectedItems(this.categories);
    this.updateFilterTypeCounts();
    this.updateTotalFilteredResults();
  }

  updateFilterTypeCounts(): void {
    // Reset all counts
    Object.keys(this.filterTypeCounts).forEach((key) => {
      this.filterTypeCounts[key] = 0;
    });

    // Count Categories (including subcategories)
    if (this.filtersData?.category) {
      this.filtersData.category.forEach((cat: any) => {
        if (cat.selected) {
          this.filterTypeCounts['Categories']++;
        }
        if (cat.children) {
          cat.children.forEach((child: any) => {
            if (child.selected) {
              this.filterTypeCounts['Categories']++;
            }
          });
        }
      });
    }

    // Count Brands
    if (this.filtersData?.make) {
      this.filtersData.make.forEach((brand: any) => {
        if (brand.selected) {
          this.filterTypeCounts['Brand']++;
        }
      });
    }

    // Count Years
    if (this.filtersData?.year) {
      this.filtersData.year.forEach((year: any) => {
        if (year.selected) {
          this.filterTypeCounts['Mfg. Year']++;
        }
      });
    }

    // Count Sellers
    if (this.filtersData?.seller) {
      this.filtersData.seller.forEach((seller: any) => {
        if (seller.selected) {
          this.filterTypeCounts['Seller']++;
        }
      });
    }

    // Count Locations
    if (this.filtersData?.location) {
      this.filtersData.location.forEach((location: any) => {
        if (location.selected) {
          this.filterTypeCounts['Location']++;
        }
      });
    }

    // Count Quality/Grades
    if (this.filtersData?.quality) {
      this.filtersData.quality.forEach((quality: any) => {
        if (quality.selected) {
          this.filterTypeCounts['Grade']++;
        }
      });
    }

    // Count Price Range
    const maxSliderValue = this.getSliderMax();
    if (
      this.selectedRadio ||
      (this.isPriceRangeModified &&
        (this.minValue > this.getSliderMin() || this.maxValue < maxSliderValue))
    ) {
      this.filterTypeCounts['Price Range'] = 1;
    }

    // Count Vehicle form selections
    if (this.vehicleForm) {
      const brand = this.vehicleForm.get('brand')?.value;
      const model = this.vehicleForm.get('model')?.value;
      const variant = this.vehicleForm.get('variant')?.value;
      const year = this.vehicleForm.get('year')?.value;

      let vehicleCount = 0;
      if (brand) vehicleCount++;
      if (model) vehicleCount++;
      if (variant) vehicleCount++;
      if (year) vehicleCount++;

      this.filterTypeCounts['Vehicle'] = vehicleCount;
    }

    // Debug logging
  }

  updateTotalFilteredResults(): void {
    // Calculate total filtered results based on selected filters
    let totalCount = 0;

    // Check if any filters are actually selected
    const hasAnyFilters = this.hasActiveFilters();

    if (!hasAnyFilters) {
      // No filters selected - show total count from all available data
      if (this.data?.total_count) {
        totalCount = this.data.total_count;
      }
      //  else if (this.filtersData?.category) {
      //   // Sum all available counts from categories
      //   this.filtersData.category.forEach((cat: any) => {
      //     totalCount += cat.count || 0;
      //     if (cat.children) {
      //       cat.children.forEach((child: any) => {
      //         totalCount += child.count || 0;
      //       });
      //     }
      //   });
      // }
    } else {
      // Filters are selected - show the filtered count
      // This represents the number of items that match the selected filters
      if (this.data?.filtered_count) {
        totalCount = this.data.filtered_count;
      } else if (this.data?.total_count) {
        // If no filtered count available, show total count as fallback
        totalCount = this.data.total_count;
      } else if (this.filtersData?.category) {
        // Count only selected items instead of summing all available counts
        totalCount = this.countSelectedItems(this.filtersData.category);
      }
    }

    this.totalFilteredResults = totalCount;

    // Debug logging
  }

  getFilterCount(filterType: string): number {
    return this.filterTypeCounts[filterType] || 0;
  }

  getTotalFilteredResults(): number {
    return this.totalFilteredResults;
  }

  getResultsDisplayText(): string {
    const hasAnyFilters = this.hasActiveFilters();

    if (!hasAnyFilters) {
      return `${this.totalFilteredResults} Total Results`;
    } else {
      return `${this.totalFilteredResults} Filtered Results`;
    }
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filterTypeCounts).some((count) => count > 0);
  }

  countSelectedItems(items: CategoryItem[]): number {
    let count = 0;

    for (const item of items) {
      if (item.selected) {
        count++;
      }

      if (item.children) {
        count += this.countSelectedItems(item.children);
      }
    }

    return count;
  }

  // LocalStorage methods for persisting filter selections
  saveFiltersToLocalStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        // Build a simple state object with only the essential data
        const selectedItems: { type: string; value: string; label: string }[] =
          [];

        // Collect all selected items from filtersData
        if (this.filtersData) {
          // Categories (following parent/subcategory logic)
          if (this.filtersData.category) {
            this.filtersData.category.forEach((cat: any) => {
              // Check if any subcategories are selected
              const hasSelectedSubcategories =
                cat.children?.some((child: any) => child.selected) || false;

              if (cat.selected && !hasSelectedSubcategories) {
                // Parent is selected and no subcategories are selected - save only parent
                selectedItems.push({
                  type: 'category',
                  value: cat.name,
                  label: cat.name,
                });
              } else if (hasSelectedSubcategories) {
                // Subcategories are selected - save only the selected subcategories, not parent
                cat.children.forEach((child: any) => {
                  if (child.selected) {
                    selectedItems.push({
                      type: 'category',
                      value: child.label || child.name,
                      label: child.label || child.name,
                    });
                  }
                });
              }
            });
          }

          // Brands (using 'make' instead of 'brand')
          if (this.filtersData.make) {
            this.filtersData.make.forEach((brand: any) => {
              if (brand.selected) {
                selectedItems.push({
                  type: 'brand',
                  value: brand.name,
                  label: brand.name,
                });
              }
            });
          }

          // Years
          if (this.filtersData.year) {
            this.filtersData.year.forEach((year: any) => {
              if (year.selected) {
                selectedItems.push({
                  type: 'year',
                  value: year.name,
                  label: year.name,
                });
              }
            });
          }

          // Quality/Grades
          if (this.filtersData.quality) {
            this.filtersData.quality.forEach((quality: any) => {
              if (quality.selected) {
                selectedItems.push({
                  type: 'quality',
                  value: quality.name,
                  label: quality.name,
                });
              }
            });
          }

          // Locations
          if (this.filtersData.location) {
            this.filtersData.location.forEach((location: any) => {
              if (location.selected) {
                selectedItems.push({
                  type: 'location',
                  value: location.name,
                  label: location.name,
                });
              }
            });
          }

          // Sellers
          if (this.filtersData.seller) {
            this.filtersData.seller.forEach((seller: any) => {
              if (seller.selected) {
                selectedItems.push({
                  type: 'seller',
                  value: seller.name,
                  label: seller.name,
                });
              }
            });
          }
        }

        const filterState = {
          selectedItems: selectedItems,
          minValue: this.minValue,
          maxValue: this.maxValue,
          selectedRadio: this.selectedRadio,
          isPriceRangeModified: this.isPriceRangeModified,
          selectedSort: this.selectedSort,
          vehicleForm: this.vehicleForm?.value,
          selectedVehicle: this.selectedVehicle,
          searchText: this.searchText,
          hasViewedResults: this.hasViewedResults,
          timestamp: Date.now(),
        };

        localStorage.setItem('filterState', JSON.stringify(filterState));
      } catch (error) {
        console.error('Error saving filter state to localStorage:', error);
      }
    }
  }

  loadFiltersFromLocalStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const savedState = localStorage.getItem('filterState');

        if (savedState) {
          const filterState = JSON.parse(savedState);
          const now = Date.now();
          const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

          // Only restore if the saved state is less than 24 hours old
          if (now - filterState.timestamp < oneDay) {
            // Store the selected items for later application
            this.selectedFilters = filterState.selectedItems || [];

            // Set price values, but ensure they're within the current API range
            const savedMinValue = filterState.minValue || 0;
            const savedMaxValue = filterState.maxValue || 10000;

            // We'll set these after the API data is loaded to ensure they're within bounds
            this.pendingMinValue = savedMinValue;
            this.pendingMaxValue = savedMaxValue;

            this.selectedRadio = filterState.selectedRadio;
            this.isPriceRangeModified =
              filterState.isPriceRangeModified || false;
            this.selectedSort = filterState.selectedSort || 'newToOld';

            // Restore search text if it exists
            // if (filterState.searchText) {
            //   this.searchText = filterState.searchText;
            // }

            // Restore hasViewedResults flag if it exists
            if (filterState.hasViewedResults !== undefined) {
              this.hasViewedResults = filterState.hasViewedResults;
            }

            // Restore vehicle form if it exists
            if (filterState.vehicleForm && this.vehicleForm) {
              this.vehicleForm.patchValue(filterState.vehicleForm);
            }

            // Restore selected vehicle if it exists
            if (filterState.selectedVehicle) {
              this.selectedVehicle = filterState.selectedVehicle;
            }

            // Don't call API here - filters will be applied after filter data is loaded in getFilterList()
          } else {
            // Clear expired filter state
            localStorage.removeItem('filterState');
          }
        }
      } catch (error) {
        console.error('Error loading filter state from localStorage:', error);
        localStorage.removeItem('filterState');
      }
    }
  }

  applySavedFiltersToData(): void {
    if (!this.filtersData) {
      return;
    }

    // Apply saved selections to filter data (following parent/subcategory logic)
    if (this.filtersData.category) {
      this.filtersData.category.forEach((cat: any) => {
        // Check if parent category is saved
        const savedParentFilter = this.selectedFilters.find(
          (f) => f.type === 'category' && f.value === cat.name,
        );

        // Check if any subcategories are saved
        const savedSubcategoryFilters = this.selectedFilters.filter(
          (f) =>
            f.type === 'category' &&
            cat.children?.some(
              (child: any) => (child.label || child.name) === f.value,
            ),
        );

        if (savedParentFilter && savedSubcategoryFilters.length === 0) {
          // Only parent is saved - select parent, deselect all subcategories
          cat.selected = true;
          if (cat.children) {
            cat.children.forEach((child: any) => {
              child.selected = false;
            });
          }
        } else if (savedSubcategoryFilters.length > 0) {
          // Subcategories are saved - deselect parent, select only saved subcategories
          cat.selected = false;
          if (cat.children) {
            cat.children.forEach((child: any) => {
              const savedChildFilter = this.selectedFilters.find(
                (f) =>
                  f.type === 'category' &&
                  f.value === (child.label || child.name),
              );
              child.selected = !!savedChildFilter;
            });
          }
        } else {
          // Nothing is saved - deselect everything
          cat.selected = false;
          if (cat.children) {
            cat.children.forEach((child: any) => {
              child.selected = false;
            });
          }
        }
      });
    }

    if (this.filtersData.make) {
      this.filtersData.make.forEach((brand: any) => {
        const savedFilter = this.selectedFilters.find(
          (f) => f.type === 'brand' && f.value === brand.name,
        );
        brand.selected = !!savedFilter;
      });
    }

    if (this.filtersData.year) {
      this.filtersData.year.forEach((year: any) => {
        const savedFilter = this.selectedFilters.find(
          (f) => f.type === 'year' && f.value === year.name,
        );
        year.selected = !!savedFilter;
      });
    }

    if (this.filtersData.quality) {
      this.filtersData.quality.forEach((quality: any) => {
        const savedFilter = this.selectedFilters.find(
          (f) => f.type === 'quality' && f.value === quality.name,
        );
        quality.selected = !!savedFilter;
      });
    }

    if (this.filtersData.location) {
      this.filtersData.location.forEach((location: any) => {
        const savedFilter = this.selectedFilters.find(
          (f) => f.type === 'location' && f.value === location.name,
        );
        location.selected = !!savedFilter;
      });
    }

    if (this.filtersData.seller) {
      this.filtersData.seller.forEach((seller: any) => {
        const savedFilter = this.selectedFilters.find(
          (f) => f.type === 'seller' && f.value === seller.name,
        );
        seller.selected = !!savedFilter;
      });
    }

    // Update selected count and filtered results
    this.updateSelectedCount();
    this.updateFilteredResultsCount();

    // After applying saved filters, update the selectedFilters array to reflect the current state
    this.updateSelectedFilters();

    // Add search chips if search text exists
    if (this.searchText && this.searchText.trim()) {
      this.addSearchChip(this.searchText.trim());
    }

    // Only call API once if we have saved filters and haven't called it yet
    if (
      this.selectedFilters &&
      this.selectedFilters.length > 0 &&
      !this.hasCalledApiFromLoad
    ) {
      this.hasCalledApiFromLoad = true;
      setTimeout(() => {
        this.updateSubjectAndApiCallFromLoadedFilters();
      }, 100);
    }
  }

  clearFiltersFromLocalStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('filterState');
    }
  }

  updateFilteredResultsCount(): void {
    // Calculate the total filtered results based on selected categories
    // This is a simple implementation - in real scenarios, you might want to do this through a service
    let totalCount = 0;

    const countSelectedItemsResults = (items: CategoryItem[]): number => {
      let count = 0;

      for (const item of items) {
        if (item.selected) {
          count += item.count;
        } else if (item.children) {
          count += countSelectedItemsResults(item.children);
        }
      }

      return count;
    };

    this.filteredResultsCount = countSelectedItemsResults(this.categories);

    // If nothing is selected, show all results
    if (this.filteredResultsCount === 0 && this.selectedCount === 0) {
      this.filteredResultsCount = this.getTotalCount(this.categories);
    }
  }

  getTotalCount(items: CategoryItem[]): number {
    return items.reduce((total, item) => total + item.count, 0);
  }

  emitSelectedCategories(): void {
    const selectedIds: string[] = [];

    const collectSelectedIds = (items: CategoryItem[]): void => {
      for (const item of items) {
        if (item.selected) {
          selectedIds.push(item.id);
        }

        if (item.children) {
          collectSelectedIds(item.children);
        }
      }
    };

    collectSelectedIds(this.categories);
    this.selectionChange.emit(selectedIds);
  }

  clearFilters(): void {
    this.resetFilterStateCore();

    // Reset sort to empty state when user clears filters manually
    this.selectedSort = '';
    this.hasViewedResults = false;

    // Clear search text
    this.searchText = '';
    this.subjectBehaviour.setSearchTerm('');

    this.updateSelectedCount();
    this.updateFilteredResultsCount();
    this.emitSelectedCategories();
    this.updateSubjectAndApiCall();
    this.clearFiltersFromLocalStorage();
    this.closeFilters();

    // Reset API call flag to allow filters to be loaded again
    this.hasCalledApiFromLoad = false;
  }

  resetFiltersForCategoryChange(): void {
    this.resetFilterStateCore();

    // Restore default sort for a fresh category view
    this.selectedSort = 'newToOld';
    this.hasViewedResults = false;

    // Always start new category without residual search text
    this.searchText = '';
    this.subjectBehaviour.setSearchTerm('');

    this.updateSelectedCount();
    this.updateFilteredResultsCount();
    this.emitSelectedCategories();
    this.updateSubjectAndApiCall();
    this.clearFiltersFromLocalStorage();

    // Reset API call flag to allow filters to be loaded again
    this.hasCalledApiFromLoad = false;
  }

  // Reset filters when searching from header (keeps search text intact)
  resetFiltersForHeaderSearch(): void {
    this.resetFilterStateCore();

    // Keep existing sort and search text when filtering from header search
    this.hasViewedResults = false;

    // DON'T clear search text - it's being set by the header

    this.updateSelectedCount();
    this.updateFilteredResultsCount();
    this.emitSelectedCategories();
    // Don't call updateSubjectAndApiCall here to avoid duplicate API calls
    this.clearFiltersFromLocalStorage();

    // Reset API call flag to allow filters to be loaded again
    this.hasCalledApiFromLoad = false;
  }

  private resetFilterStateCore(): void {
    const clearSelection = (items?: CategoryItem[]): void => {
      if (!items?.length) {
        return;
      }
      for (const item of items) {
        item.selected = false;
        if (item.children) {
          clearSelection(item.children);
        }
      }
    };

    // Reset tree selections maintained locally
    clearSelection(this.categories);

    // Clear all filter data selections (including subcategories)
    if (this.filtersData) {
      if (this.filtersData.category) {
        this.filtersData.category.forEach((cat: any) => {
          cat.selected = false;
          if (cat.children) {
            cat.children.forEach((child: any) => {
              child.selected = false;
            });
          }
        });
      }
      if (this.filtersData.make) {
        this.filtersData.make.forEach((brand: any) => {
          brand.selected = false;
        });
      }
      if (this.filtersData.year) {
        this.filtersData.year.forEach((year: any) => {
          year.selected = false;
        });
      }
      if (this.filtersData.quality) {
        this.filtersData.quality.forEach((quality: any) => {
          quality.selected = false;
        });
      }
      if (this.filtersData.location) {
        this.filtersData.location.forEach((location: any) => {
          location.selected = false;
        });
      }
      if (this.filtersData.seller) {
        this.filtersData.seller.forEach((seller: any) => {
          seller.selected = false;
        });
      }
    }

    // Clear vehicle form
    if (this.vehicleForm) {
      this.vehicleForm.reset();
      // Explicitly enable all dropdowns when clearing filters
      this.vehicleForm.get('brand')?.enable({ emitEvent: false });
      this.vehicleForm.get('model')?.enable({ emitEvent: false });
      this.vehicleForm.get('year')?.enable({ emitEvent: false });
      this.vehicleForm.get('variant')?.enable({ emitEvent: false });
    }

    // Reset registration field filled flag
    this.isRegistrationFieldFilled = false;

    // Clear selected vehicle
    this.selectedVehicle = null;

    // Reset price range to API values
    const sliderMin = this.getSliderMin();
    const sliderMax = this.getSliderMax();
    this.minValue = sliderMin;
    this.maxValue = sliderMax;
    this.selectedRadio = null;
    this.isPriceRangeModified = false;
    this.pendingMinValue = undefined;
    this.pendingMaxValue = undefined;

    // Clear selected filters array
    this.selectedFilters = [];

    // Reset recorded filter counts
    Object.keys(this.filterTypeCounts).forEach((key) => {
      this.filterTypeCounts[key] = 0;
    });
  }

  getFilterList() {
    this.loaderService.show();
    try {
      let data = {
        limit: 50,
        offset: 0,
        search_text: this.data ? this.data.search_text : '',
        filter_type: this.partType,
        make_list:
          this.partType === 'Used Part'
            ? this.data
              ? this.data?.make || this.data?.VEHICLE_MAKE_s
              : ''
            : '',
        model_list:
          this.partType === 'Used Part'
            ? this.data
              ? this.data?.model || this.data?.PARENT_MODEL_s
              : ''
            : '',
        variant_list:
          this.partType === 'Used Part'
            ? this.data
              ? this.data?.variant
              : this.data?.VARIANT_s
            : '',
        year_of_manufacture_list:
          this.partType === 'Used Part'
            ? this.data
              ? this.data?.year_of_manufacturing
              : this.data?.YEAR_OF_MANUFACTURE_s
            : '',
        seller_code_list: this.data ? this.data?.seller : '',
        grade_list:
          this.partType === 'Used Part'
            ? this.data
              ? this.data?.quality
              : ''
            : '',
        category_list: this.data
          ? this.data?.name
            ? this.data?.name
            : this.data?.seller_code
          : '',
        location_list:
          this.partType === 'Scrap Part'
            ? this.data
              ? this.data?.location
              : ''
            : '',
        // category: this.data ? this.data?.category : '',
        sub_category_list:
          this.partType === 'Scrap Part'
            ? this.data
              ? this.data?.sub_category_a
              : ''
            : '',
        // seller_code_list:this.partType === 'Scrap Part' ? this.data ? this.data?.seller_code : '' : '',
        // "sub_category_a": "TABLE_NAME_s",
      };

      this.sub.add(
        this.apiService.getFilters(data).subscribe({
          next: (value) => {
            if (value.success) {
              // Handle the new API response structure
              this.filtersData = value.data.filters;

              // Map the new structure to existing properties for backward compatibility
              this.brands = value?.data?.filters?.make || [];
              // Only set price values if not already modified by user
              if (!this.isPriceRangeModified) {
                this.minValue =
                  value?.data?.filters?.price_slider?.min_price || 0;
                this.maxValue =
                  value?.data?.filters?.price_slider?.max_price || 10000;
              }

              // Process and map category data to include subcategories as children
              this.processCategoryData();

              // Process price range data from API
              this.processPriceRangeData();

              // Update slider values from API
              this.updateSliderValues();

              // Build dynamic filters based on API response
              this.buildDynamicFilters();

              // this.productData = value.data.part_list;
              // Initialize carousel properties for each product

              // Apply saved filters to the loaded data
              this.applySavedFiltersToData();

              // Update filter counts and results after loading data
              this.updateSelectedCount();
              this.updateTotalFilteredResults();

              // Don't call updateSelectedFilters here as it's already called in applySavedFiltersToData
            } else {
              this.filtersData = {};
              this.brands = [];
            }
            this.loaderService.hide();
          },
          error: (err) => {
            console.error('Error loading products:', err);
            this.loaderService.hide();
          },
          complete: () => {
            this.loaderService.hide();
          },
        }),
      );
    } catch (error) {
      console.error('Error in products:', error);
      this.loaderService.show();

      throw error;
    }
  }

  processCategoryData() {
    // Map API subcategories structure to children structure for compatibility
    if (
      this.filtersData?.category &&
      Array.isArray(this.filtersData.category)
    ) {
      this.filtersData.category.forEach((category: any) => {
        // Initialize selection and expansion properties
        category.selected = false;
        category.expanded = false;

        // Map subcategories to children if they exist
        if (category.subcategories && Array.isArray(category.subcategories)) {
          category.children = category.subcategories.map((subcategory: any) => {
            return {
              name: subcategory.name,
              label: subcategory.name,
              count: subcategory.count,
              selected: false,
              expanded: false,
              children: [], // No deeper nesting in current API structure
            };
          });
        } else {
          category.children = [];
        }
      });
    }

    // Process other filter data to ensure they have selection properties
    if (
      this.filtersData?.location &&
      Array.isArray(this.filtersData.location)
    ) {
      this.filtersData.location.forEach((location: any) => {
        location.selected = false;
      });
    }

    if (this.filtersData?.make && Array.isArray(this.filtersData.make)) {
      this.filtersData.make.forEach((make: any) => {
        make.selected = false;
      });
    }

    if (this.filtersData?.year && Array.isArray(this.filtersData.year)) {
      this.filtersData.year.forEach((year: any) => {
        year.selected = false;
      });
    }

    if (this.filtersData?.seller && Array.isArray(this.filtersData.seller)) {
      this.filtersData.seller.forEach((seller: any) => {
        seller.selected = false;
      });
    }

    if (this.filtersData?.quality && Array.isArray(this.filtersData.quality)) {
      this.filtersData.quality.forEach((quality: any) => {
        quality.selected = false;
      });
    }
  }

  processPriceRangeData() {
    if (
      this.filtersData?.price_range &&
      Array.isArray(this.filtersData.price_range)
    ) {
      this.radioOptions = this.filtersData.price_range.map((range: any) => {
        const min = range.range[0];
        const max = range.range[1];

        // Format the label based on the range
        let label = '';
        if (max === null) {
          label = `₹${min.toLocaleString()} +`;
        } else {
          label = `₹${min.toLocaleString()} – ₹${max.toLocaleString()}`;
        }

        return {
          min: min,
          max: max,
          label: label,
          count: range.count,
        };
      });
    } else {
      // Fallback to default options if API doesn't provide price range
      this.radioOptions = [];
    }
  }

  updateSliderValues() {
    if (this.filtersData?.price_slider) {
      const apiMin = this.filtersData.price_slider.min_price || 0;
      const apiMax = this.filtersData.price_slider.max_price || 10000;

      // If we have pending values from localStorage, use them if they're within bounds
      if (
        this.pendingMinValue !== undefined &&
        this.pendingMaxValue !== undefined
      ) {
        this.minValue = Math.max(
          apiMin,
          Math.min(this.pendingMinValue, apiMax),
        );
        this.maxValue = Math.max(
          apiMin,
          Math.min(this.pendingMaxValue, apiMax),
        );
        this.pendingMinValue = undefined;
        this.pendingMaxValue = undefined;
      } else {
        this.minValue = apiMin;
        this.maxValue = apiMax;
      }
    }
  }

  getSliderMin(): number {
    return this.filtersData?.price_slider?.min_price || 0;
  }

  getSliderMax(): number {
    return this.filtersData?.price_slider?.max_price || 10000;
  }

  getSliderStep(): number {
    const max = this.getSliderMax();
    const min = this.getSliderMin();
    const range = max - min;
    // Return a reasonable step size based on the range
    if (range <= 1000) return 10;
    if (range <= 10000) return 100;
    if (range <= 100000) return 1000;
    return 10000;
  }

  formatPrice(price: number): string {
    if (price === null || price === undefined) {
      return '0';
    }

    // Round to 2 decimal places for display
    const roundedPrice = Math.round(price * 100) / 100;

    // Format with commas for thousands and show 2 decimal places
    return roundedPrice.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  toggleSelectionBrand(category: any, event: Event) {
    category.selected = (event.target as HTMLInputElement).checked;
    this.updateSelectedFilters();
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();
  }

  filteredCategories() {
    if (!this.categorySearch) return this.brands;

    const search = this.categorySearch.toLowerCase();
    return this.brands.filter((cat: any) =>
      cat.name.toLowerCase().includes(search),
    );
  }

  filteredSellerCategories() {
    if (!this.sellerSearch?.trim()) {
      return this.filtersData?.seller || [];
    }

    const search = this.sellerSearch.toLowerCase();

    return this.filtersData?.seller?.filter((category: any) =>
      category.name?.toLowerCase().includes(search),
    );
  }

  toggleSelectionSeller(category: any, event: Event) {
    category.selected = (event.target as HTMLInputElement).checked;
    this.updateSelectedFilters();
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();
  }

  filteredLocations(): any[] {
    if (!this.filtersData?.location) return [];
    if (!this.locationSearch.trim()) return this.filtersData.location;

    const search = this.locationSearch.toLowerCase();
    return this.filtersData.location.filter((loc: any) =>
      loc.name.toLowerCase().includes(search),
    );
  }

  toggleLocation(location: any): void {
    location.selected = !location.selected;
    this.updateSelectedFilters();
    this.updateSelectedCount(); // Add this line
    this.saveFiltersToLocalStorage();
    // You can emit an event or update filters here if needed
  }

  closeFilters(): void {
    this.filterClose.emit();
    this.Sidefilter = false;
    this.modalService.closeModal('filter-sidebar');
  }

  getGarage(): void {
    this.loaderService.show();
    const payload = {
      limit: 10,
      offset: 0,
      search_text: '',
      sort_by: '',
    };

    this.sub.add(
      this.apiService.get_garage(payload).subscribe({
        next: (res) => {
          if (res?.success) {
            this.cdr.detectChanges();
            this.IMAGES = res?.data;
            this.loaderService.hide();
            // Handle successful data here
            // For example: this.garageList = res.data;
          } else {
            this.loaderService.hide();

            console.warn(
              'Garage API failed with message:',
              res?.message || 'Unknown error',
            );
          }
        },
        error: (err) => {
          this.loaderService.hide();

          console.error('Error fetching garage data:', err);
        },
      }),
    );
  }

  getBrandList() {
    const payload = {
      action_type: 'make',
      make_list: [],
      model_list: [],
      variant_list: [],
      year_of_manufacture: '',
    };

    this.sub.add(
      this.apiService.getBrand(payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.brandslist = res?.data?.Vehicle_Brands_List;
          } else {
            this.brands = [];
          }
        },
        error: (err) => {
          console.error('Failed to fetch  brand', err);
        },
      }),
    );
  }

  onBrandSelect(data: any) {
    const brand = this.vehicleForm.get('brand')?.value;

    const payload = {
      action_type: 'model',
      make_list: [brand],
      model_list: [],
      variant_list: [],
      year_of_manufacture: '',
    };

    this.sub.add(
      this.apiService.getBrand(payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.modelList = res?.data?.Vehicle_Models_List;
          } else {
            this.modelList = [];
          }
          // Optionally show success message or update UI
        },
        error: (err) => {
          console.error('Failed to add vehicle', err);
        },
      }),
    );

    // Update selected filters after brand selection
    this.updateSelectedFilters();
    this.updateSelectedCount(); // Add this line
  }

  onModelSelect(data: any) {
    const model = this.vehicleForm.get('model')?.value;
    const brand = this.vehicleForm.get('brand')?.value;

    const payload = {
      action_type: 'year_of_manufacture',
      make_list: [brand],
      model_list: [model],
      variant_list: [],
      year_of_manufacture: '',
    };

    this.sub.add(
      this.apiService.getBrand(payload).subscribe({
        next: (res) => {
          if (res.success) {
            const rawYears = res.data?.Vehicle_Year || [];
            //this.varientList = res?.data?.Vehicle_Variant_List;
            // Extract, sort numerically, then convert to string
            this.yearList = rawYears
              .map(
                (item: { YEAR_OF_MANUFACTURE_s: string }) =>
                  item.YEAR_OF_MANUFACTURE_s,
              )
              .sort((a: string, b: string) => +b - +a) // numeric descending
              .map((y: string) => y.toString()); // ✅ force string
          } else {
            this.yearList = [];
          }
          // Optionally show success message or update UI
        },
        error: (err) => {
          console.error('Failed to add vehicle', err);
        },
      }),
    );

    // Update selected filters after model selection
    this.updateSelectedFilters();
    this.updateSelectedCount(); // Add this line
  }

  onYearSelect() {
    const brand = this.vehicleForm.get('brand')?.value;
    const model = this.vehicleForm.get('model')?.value;
    const year = this.vehicleForm.get('year')?.value;
    const payload = {
      action_type: 'variant',
      make_list: [brand],
      model_list: [model],
      variant_list: [],
      year_of_manufacture: year,
      offset: 0,
      limit: 10,
    };

    this.sub.add(
      this.apiService.getBrand(payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.varientList = res.data?.Vehicle_Variant_List.map(
              (item: { VARIANT_s: string }) => item.VARIANT_s,
            );
          } else {
            this.varientList = [];
          }
        },
        error: (err) => console.error('Failed to fetch years', err),
      }),
    );

    // Update selected filters after year selection
    this.updateSelectedFilters();
    this.updateSelectedCount();
  }

  onVariantSelect() {
    const brand = this.vehicleForm.get('brand')?.value;
    const model = this.vehicleForm.get('model')?.value;
    const year = this.vehicleForm.get('year')?.value;
    const variant = this.vehicleForm.get('variant')?.value;
    const payload = {
      action_type: 'Get Vehicle Details',
      make_list: [brand],
      model_list: [model],
      variant_list: [variant],
      year_of_manufacture: year,
      offset: 0,
      limit: 10,
    };

    this.sub.add(
      this.apiService.getBrand(payload).subscribe({
        next: (res) => {
          if (res?.success) {
            // API returned success
            this.previewData = res?.data?.[0] || {};
            // this.showConfirmation = true;
          } else {
            console.warn('No data found:', res?.data?.msg);
          }
        },
        error: (err) => {
          console.error('Failed to proceed', err);
        },
      }),
    );

    // Update selected filters after variant selection
    this.updateSelectedFilters();
    this.updateSelectedCount();
  }

  // Method to enable all form fields
  enableAllFields() {
    if (this.vehicleForm) {
      this.vehicleForm.get('brand')?.enable();
      this.vehicleForm.get('model')?.enable();
      this.vehicleForm.get('year')?.enable();
      this.vehicleForm.get('variant')?.enable();
      this.vehicleForm.get('registrationNo')?.enable();
    }
  }

  // Method to enable validation for registration number (10 alphanumeric characters, no leading spaces)
  enableRegistrationNoValidation() {
    const registrationNoControl = this.vehicleForm.get('registrationNo');
    if (registrationNoControl) {
      registrationNoControl.setValidators([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern(/^[A-Za-z0-9]{10}$/), // No leading spaces allowed
      ]);
      registrationNoControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Method to handle input and prevent leading spaces
  onRegistrationNoInput(event: any) {
    let value = event.target.value || '';
    const originalValue = value;

    // Remove leading spaces only
    value = value.replace(/^\s+/, '');

    // Convert to uppercase
    value = value.toUpperCase();

    // If value changed (leading spaces were removed or case changed), update the form control and input
    if (value !== originalValue) {
      // Update the form control value without leading spaces and in uppercase
      this.vehicleForm
        .get('registrationNo')
        ?.setValue(value, { emitEvent: false });
      // Update the input element value
      event.target.value = value;
    }

    // Call the change handler
    this.onRegistrationNoChange();
  }

  // Method to disable validation for registration number
  disableRegistrationNoValidation() {
    const registrationNoControl = this.vehicleForm.get('registrationNo');
    if (registrationNoControl) {
      registrationNoControl.clearValidators();
      registrationNoControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Method to disable all form fields
  disableAllFields() {
    if (this.vehicleForm) {
      this.vehicleForm.get('brand')?.disable();
      this.vehicleForm.get('model')?.disable();
      this.vehicleForm.get('year')?.disable();
      this.vehicleForm.get('variant')?.disable();
      // this.vehicleForm.get('registrationNo')?.disable();

      // Remove validation from registration number when vehicle is found
      // this.disableRegistrationNoValidation();
    }
  }

  onRegistrationNoChange(registrationNo?: string) {
    // Get the current registration number value from form if not provided
    const regNo =
      registrationNo?.trim() ||
      this.vehicleForm.get('registrationNo')?.value?.trim() ||
      '';

    // Immediately disable/enable dropdowns based on registration field value
    this.isRegistrationFieldFilled = regNo.length > 0;

    if (this.isRegistrationFieldFilled) {
      // Disable all dropdowns immediately when registration has any value
      this.vehicleForm.get('brand')?.disable({ emitEvent: false });
      this.vehicleForm.get('model')?.disable({ emitEvent: false });
      this.vehicleForm.get('year')?.disable({ emitEvent: false });
      this.vehicleForm.get('variant')?.disable({ emitEvent: false });
    } else {
      // Enable all dropdowns when registration is cleared
      this.vehicleForm.get('brand')?.enable({ emitEvent: false });
      this.vehicleForm.get('model')?.enable({ emitEvent: false });
      this.vehicleForm.get('year')?.enable({ emitEvent: false });
      this.vehicleForm.get('variant')?.enable({ emitEvent: false });
    }

    // Update selected filters when registration number changes
    this.updateSelectedFilters();
    this.updateSelectedCount();

    // If registration number is cleared or less than 10 characters
    if (!regNo || regNo.length < 10) {
      // Reset form values that were bound from API
      this.vehicleForm.patchValue(
        {
          brand: '',
          model: '',
          variant: '',
          year: '',
        },
        { emitEvent: false },
      );

      // Clear dependent dropdowns
      this.modelList = [];
      this.varientList = [];
      this.yearList = [];

      // Remove validation (user hasn't entered 10 chars yet)
      this.disableRegistrationNoValidation();
      this.isRegistrationNoEntered = false;
      this.noVehicleFound = false; // Reset error message when field is cleared
      return;
    }

    // Only call API when exactly 10 characters are entered
    if (regNo.length === 10) {
      // Validate alphanumeric pattern before API call
      const alphanumericPattern = /^[A-Za-z0-9]{10}$/;
      if (!alphanumericPattern.test(regNo)) {
        // Invalid format - enable validation to show error
        // Keep dropdowns disabled since registration still has a value
        this.vehicleForm.get('registrationNo')?.enable({ emitEvent: false });
        this.enableRegistrationNoValidation();
        this.noVehicleFound = false;
        this.isRegistrationNoEntered = true;
        return;
      }

      const payload = {
        reg_no: regNo,
        action_type: 'Get Vehicle Details',
      };

      this.loaderService.show();
      this.isRegistrationNoEntered = true;

      this.sub.add(
        this.apiService.getBrand(payload).subscribe({
          next: (res: any) => {
            this.loaderService.hide();

            if (res?.success && res.data?.Vehicle_Data?.[0]) {
              const vehicle = res.data.Vehicle_Data[0];
              const brandValue = vehicle?.VEHICLE_MAKE_s || '';
              const modelValue = vehicle?.PARENT_MODEL_s || '';
              const yearValue = vehicle?.YEAR_OF_MANUFACTURE_s
                ? String(vehicle.YEAR_OF_MANUFACTURE_s)
                : '';
              const variantValue = vehicle?.VARIANT_s || '';

              // Ensure brandslist is loaded before setting brand value
              if (!this.brandslist || this.brandslist.length === 0) {
                this.getBrandList();
              }

              // First, set the brand value and load model list
              this.vehicleForm
                .get('brand')
                ?.setValue(brandValue, { emitEvent: false });

              if (brandValue) {
                // Load model list based on brand
                const modelPayload = {
                  action_type: 'model',
                  make_list: [brandValue],
                  model_list: [],
                  variant_list: [],
                  year_of_manufacture: '',
                };

                this.sub.add(
                  this.apiService.getBrand(modelPayload).subscribe({
                    next: (modelRes: any) => {
                      if (modelRes.success) {
                        this.modelList =
                          modelRes?.data?.Vehicle_Models_List || [];

                        // Set model value after model list is loaded
                        this.vehicleForm
                          .get('model')
                          ?.setValue(modelValue, { emitEvent: false });

                        if (modelValue && yearValue) {
                          // Load year list based on brand and model
                          const yearPayload = {
                            action_type: 'year_of_manufacture',
                            make_list: [brandValue],
                            model_list: [modelValue],
                            variant_list: [],
                            year_of_manufacture: '',
                          };

                          this.sub.add(
                            this.apiService.getBrand(yearPayload).subscribe({
                              next: (yearRes: any) => {
                                if (yearRes.success) {
                                  const rawYears =
                                    yearRes.data?.Vehicle_Year || [];
                                  this.yearList = rawYears
                                    .map(
                                      (item: {
                                        YEAR_OF_MANUFACTURE_s: string;
                                      }) => item.YEAR_OF_MANUFACTURE_s,
                                    )
                                    .sort((a: string, b: string) => +b - +a)
                                    .map((y: string) => y.toString());

                                  // Set year value after year list is loaded
                                  this.vehicleForm
                                    .get('year')
                                    ?.setValue(yearValue, { emitEvent: false });

                                  if (variantValue) {
                                    // Load variant list based on brand, model, and year
                                    const variantPayload = {
                                      action_type: 'variant',
                                      make_list: [brandValue],
                                      model_list: [modelValue],
                                      variant_list: [],
                                      year_of_manufacture: yearValue,
                                      offset: 0,
                                      limit: 10,
                                    };

                                    this.sub.add(
                                      this.apiService
                                        .getBrand(variantPayload)
                                        .subscribe({
                                          next: (variantRes: any) => {
                                            if (variantRes.success) {
                                              this.varientList =
                                                variantRes.data?.Vehicle_Variant_List?.map(
                                                  (item: {
                                                    VARIANT_s: string;
                                                  }) => item.VARIANT_s,
                                                ) || [];

                                              // Set variant value after variant list is loaded
                                              this.vehicleForm
                                                .get('variant')
                                                ?.setValue(variantValue, {
                                                  emitEvent: false,
                                                });

                                              // Update validity and selected filters
                                              this.vehicleForm.updateValueAndValidity(
                                                { emitEvent: false },
                                              );
                                              this.updateSelectedFilters();
                                              this.updateSelectedCount();

                                              // Trigger change detection to update the UI
                                              this.cdr.detectChanges();
                                            }
                                          },
                                          error: (err: any) => {
                                            console.error(
                                              'Error loading variant list:',
                                              err,
                                            );
                                            this.vehicleForm
                                              .get('variant')
                                              ?.setValue(variantValue, {
                                                emitEvent: false,
                                              });
                                            this.vehicleForm.updateValueAndValidity(
                                              { emitEvent: false },
                                            );
                                            this.updateSelectedFilters();
                                            this.updateSelectedCount();
                                            this.cdr.detectChanges();
                                          },
                                        }),
                                    );
                                  } else {
                                    this.vehicleForm
                                      .get('variant')
                                      ?.setValue('', { emitEvent: false });
                                    this.vehicleForm.updateValueAndValidity({
                                      emitEvent: false,
                                    });
                                    this.updateSelectedFilters();
                                    this.updateSelectedCount();
                                    this.cdr.detectChanges();
                                  }
                                }
                              },
                              error: (err: any) => {
                                console.error('Error loading year list:', err);
                                this.vehicleForm
                                  .get('year')
                                  ?.setValue(yearValue, { emitEvent: false });
                                this.vehicleForm
                                  .get('variant')
                                  ?.setValue(variantValue, {
                                    emitEvent: false,
                                  });
                                this.vehicleForm.updateValueAndValidity({
                                  emitEvent: false,
                                });
                                this.updateSelectedFilters();
                                this.updateSelectedCount();
                                this.cdr.detectChanges();
                              },
                            }),
                          );
                        } else {
                          this.vehicleForm
                            .get('year')
                            ?.setValue(yearValue, { emitEvent: false });
                          this.vehicleForm
                            .get('variant')
                            ?.setValue(variantValue, { emitEvent: false });
                          this.vehicleForm.updateValueAndValidity({
                            emitEvent: false,
                          });
                          this.updateSelectedFilters();
                          this.updateSelectedCount();
                          this.cdr.detectChanges();
                        }
                      }
                    },
                    error: (err: any) => {
                      console.error('Error loading model list:', err);
                      // Still set the values even if API fails
                      this.vehicleForm
                        .get('model')
                        ?.setValue(modelValue, { emitEvent: false });
                      this.vehicleForm
                        .get('year')
                        ?.setValue(yearValue, { emitEvent: false });
                      this.vehicleForm
                        .get('variant')
                        ?.setValue(variantValue, { emitEvent: false });
                      this.vehicleForm.updateValueAndValidity({
                        emitEvent: false,
                      });
                      this.updateSelectedFilters();
                      this.updateSelectedCount();
                      this.cdr.detectChanges();
                    },
                  }),
                );
              } else {
                // No brand value, just set empty values
                this.vehicleForm
                  .get('model')
                  ?.setValue('', { emitEvent: false });
                this.vehicleForm
                  .get('year')
                  ?.setValue('', { emitEvent: false });
                this.vehicleForm
                  .get('variant')
                  ?.setValue('', { emitEvent: false });
                this.vehicleForm.updateValueAndValidity({ emitEvent: false });
                this.updateSelectedFilters();
                this.updateSelectedCount();
                this.cdr.detectChanges();
              }

              // Keep registration number enabled so user can edit it
              // Remove validation since vehicle was found successfully
              this.disableRegistrationNoValidation();
              this.noVehicleFound = false; // Reset error message when vehicle is found
              this.isRegistrationNoEntered = true;
            } else {
              // If API fails or vehicle not found, keep dropdowns disabled (as long as registration has value)
              // Only enable validation for registration field
              this.enableRegistrationNoValidation();
              this.noVehicleFound = true; // Show error message
              // Keep dropdowns disabled since registration still has a value
            }
          },

          error: (error: any) => {
            this.loaderService.hide();
            console.error('Error fetching vehicle details:', error);
            // On error, keep dropdowns disabled (as long as registration has value)
            // Only enable validation for registration field
            this.enableRegistrationNoValidation();
            this.noVehicleFound = true; // Show error message on API error
            // Keep dropdowns disabled since registration still has a value
          },
        }),
      );
    }
  }

  // Search functionality methods
  onSearchInput(event: any) {
    this.searchText = event.target.value;
  }

  onSearchSubmit() {
    if (this.searchText && this.searchText.trim()) {
      const searchTerm = this.searchText.trim();

      // Add search text as a filter chip (this will replace any existing search chip)
      this.addSearchChip(searchTerm);

      // Set flag to show selected filters immediately after search
      this.hasViewedResults = true;

      // Emit search text through behaviour subject service
      this.subjectBehaviour.setSearchTerm(searchTerm);

      // Update selected count and save filters
      this.updateSelectedCount();
      this.saveFiltersToLocalStorage();

      // Trigger API call with the search term
      this.updateSubjectAndApiCall();

      // Clear the input field
      this.searchText = '';
    }
  }

  onSearchKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onSearchSubmit();
    }
  }

  addSearchChip(searchTerm: string) {
    // Validate search term before adding
    if (!searchTerm || searchTerm.trim() === '') {
      return;
    }

    // Remove any existing search chips first
    this.selectedFilters = this.selectedFilters.filter(
      (filter: any) => filter.type !== 'search',
    );

    // Add the new search chip
    this.selectedFilters.push({
      type: 'search',
      value: searchTerm.trim(),
      label: searchTerm.trim(),
    });
  }

  removeSearchChip(searchTerm: string) {
    // Remove search chip from selectedFilters
    this.selectedFilters = this.selectedFilters.filter(
      (filter: any) =>
        !(filter.type === 'search' && filter.value === searchTerm),
    );

    // Clear search text from input field
    this.searchText = '';

    // Clear search term from behaviour subject service
    this.subjectBehaviour.setSearchTerm('');

    // Update selected count and save filters
    this.updateSelectedCount();
    this.saveFiltersToLocalStorage();

    // Update subject behavior and trigger API call with cleared search
    this.updateSubjectAndApiCall();
  }

  // Method to update BehaviorSubject when filters are loaded from localStorage on page refresh
  private updateSubjectAndApiCallFromLoadedFilters(): void {
    if (this.selectedFilters && this.selectedFilters.length > 0) {
      // Build filter object from selected filters
      const filters = this.buildFilterObjectFromSelectedFilters();

      // Update BehaviorSubject with loaded filters
      this.subjectBehaviour.setSelectedFilters(filters);

      // Trigger API call with loaded filters
      this.makeApiCallWithFilters(filters);
    }
  }

  // Helper method to build filter object from selectedFilters array
  private buildFilterObjectFromSelectedFilters(): any {
    const filters: any = {
      categories: [],
      subcategories: [],
      brands: [],
      years: [],
      grades: [],
      locations: [],
      sellers: [],
      vehicles: [],
      price_range: null,
      sort_by: this.selectedSort,
      search_text: '',
      variant: this.selectedVehicle?.variant,
      model:
        this.vehicleForm?.get('model')?.value || this.selectedVehicle?.model,
    };

    // Process each selected filter
    this.selectedFilters.forEach((filter: any) => {
      switch (filter.type) {
        case 'category':
          // Check if it's a subcategory or main category
          let isSubcategory = false;
          if (this.filtersData?.category) {
            for (const cat of this.filtersData.category) {
              if (
                cat.children?.some(
                  (child: any) => (child.label || child.name) === filter.value,
                )
              ) {
                filters.subcategories.push(filter.value);
                isSubcategory = true;
                break;
              }
            }
          }
          if (!isSubcategory) {
            filters.categories.push(filter.value);
          }
          break;
        case 'brand':
          filters.brands.push(filter.value);
          break;
        case 'year':
          filters.years.push(filter.value);
          break;
        case 'quality':
          filters.grades.push(filter.value);
          break;
        case 'location':
          filters.locations.push(filter.value);
          break;
        case 'seller':
          filters.sellers.push(filter.value);
          break;
        case 'vehicle':
          filters.vehicles.push(filter.value);
          break;
        case 'search':
          if (filters.search_text) {
            filters.search_text += ' ' + filter.value;
          } else {
            filters.search_text = filter.value;
          }
          break;
      }
    });

    // Add price range if modified
    if (
      this.selectedRadio ||
      (this.isPriceRangeModified &&
        (this.minValue > this.getSliderMin() ||
          this.maxValue < this.getSliderMax()))
    ) {
      filters.price_range = this.selectedRadio
        ? [this.selectedRadio.min, this.selectedRadio.max]
        : [this.minValue, this.maxValue];
    }

    return filters;
  }

  // Vehicle selection methods
  selectVehicle(vehicle: any) {
    // Toggle selection - if clicking the same vehicle, deselect it
    if (this.selectedVehicle && this.selectedVehicle.make === vehicle.make) {
      this.selectedVehicle = null;
    } else {
      this.selectedVehicle = vehicle;
    }

    // Update selected filters when vehicle is selected/deselected
    this.updateSelectedFilters();
    this.updateSelectedCount();
    this.saveFiltersToLocalStorage();
  }

  isVehicleSelected(vehicle: any): boolean {
    return this.selectedVehicle && this.selectedVehicle.make === vehicle.make;
  }

  // Test method to verify dynamic filters work correctly
  // testDynamicFilters() {
  //   // Simulate the API response structure
  //   const testApiResponse = {
  //     "filters": {
  //       "location": [
  //         {
  //           "name": "KHORDHA",
  //           "count": 5
  //         }
  //       ],
  //       "price_range": [
  //         {
  //           "range": [0, 1000],
  //           "name": "0–1000",
  //           "count": 3
  //         },
  //         {
  //           "range": [1001, 3000],
  //           "name": "1001–3000",
  //           "count": 2
  //         }
  //       ],
  //       "category": [
  //         {
  //           "name": "Ferrous",
  //           "count": 5,
  //           "subcategories": [
  //             {
  //               "name": "Alloyed Steel - HMS",
  //               "count": 5
  //             }
  //           ]
  //         }
  //       ],
  //       "price_slider": {
  //         "min_price": 384,
  //         "max_price": 1280
  //       }
  //     }
  //   };

  //   // Set the test data
  //   this.filtersData = testApiResponse.filters;

  //   // Process the data
  //   this.processCategoryData();
  //   this.processPriceRangeData();
  //   this.updateSliderValues();
  //   this.buildDynamicFilters();

  //   // Log the results for verification
  //   console.log('Dynamic filters built:', this.filters);
  //   console.log('Filter data processed:', this.filtersData);
  //   console.log('Price range options:', this.radioOptions);
  //   console.log('Slider values:', { min: this.minValue, max: this.maxValue });

  //   return {
  //     filters: this.filters,
  //     filtersData: this.filtersData,
  //     radioOptions: this.radioOptions,
  //     sliderValues: { min: this.minValue, max: this.maxValue }
  //   };
  // }
}

export type ViewMode = 'grid' | 'list';
interface CategoryItem {
  name: any;
  id: string;
  label: string;
  count: number;
  selected: boolean;
  expanded: boolean;
  children?: CategoryItem[];
}
